import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRightIcon, SparklesIcon, ClockIcon, UserIcon, FaceSmileIcon } from '@heroicons/react/24/outline'
import { PRODUCTS, UNITS_PER_BOX } from '../data/products'
import { motion } from 'framer-motion'
import BulkOffersBanner from '../components/BulkOffersBanner'
import PriceBanner from '../components/PriceBanner'
import { useCart } from '../context/CartContext'
import toast from 'react-hot-toast'
import api from '../config/api'

const Shop = () => {
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState('all')
  const [products, setProducts] = useState(PRODUCTS)
  const [loadingProducts, setLoadingProducts] = useState(true)

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/admin_api.php?action=get_products')
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          setProducts(response.data.data)
        }
      } catch (err) {
        console.warn('Could not fetch products from database, falling back to local data.', err)
      } finally {
        setLoadingProducts(false)
      }
    }
    fetchProducts()
  }, [])

  const categories = [
    { id: 'all', label: 'All Products' },
    { id: 'adult', label: 'Adult' },
    { id: 'kids', label: 'Kids' },
  ]

  const filtered = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory)

  const adultProducts = filtered.filter(p => p.category === 'adult')
  const kidsProducts = filtered.filter(p => p.category === 'kids')

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Page Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">Our Bamboo Toothbrushes</h1>
            <p className="mt-3 text-gray-500 text-base leading-relaxed">
              All products sold in boxes of {UNITS_PER_BOX} units. Minimum order: 1 box. Shipping across all of Canada.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mt-8" role="tablist" aria-label="Product categories">
            {categories.map(cat => (
              <button
                key={cat.id}
                id={`shop-cat-${cat.id}`}
                role="tab"
                aria-selected={activeCategory === cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeCategory === cat.id
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <PriceBanner />
        <BulkOffersBanner className="mt-4" />
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Adult Section */}
        {adultProducts.length > 0 && (
          <div className="mb-14">
            {activeCategory === 'all' && (
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-4 h-4 text-primary-600" />
                </span>
                Adult Bamboo Toothbrushes
              </h2>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {adultProducts.map((product, i) => (
                <ShopProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Kids Section */}
        {kidsProducts.length > 0 && (
          <div>
            {activeCategory === 'all' && (
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                  <FaceSmileIcon className="w-4 h-4 text-pink-600" />
                </span>
                Kids Bamboo Toothbrushes
              </h2>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {kidsProducts.map((product, i) => (
                <ShopProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delivery Notice Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <ClockIcon className="w-8 h-8 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">Delivery Timeline</p>
            <p className="text-amber-700 text-sm mt-0.5">
              All orders ship within <strong>3–4 weeks</strong> once confirmed. Shipping available <strong>across all of Canada</strong>.
              <strong>Free shipping</strong> on orders of 2+ boxes (otherwise $15.00 standard shipping).
            </p>
          </div>
        </div>
      </div>

    </div>
  )
}

const ShopProductCard = ({ product, index }) => {
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const [added, setAdded] = useState(false)

  const handleAdd = (e) => {
    e.stopPropagation()
    addToCart(product, 1, null, null)
    setAdded(true)
    toast.success(`${product.fullName} added to cart!`)
    setTimeout(() => setAdded(false), 2000)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      onClick={() => navigate(`/shop/${product.slug}`)}
      className="cursor-pointer bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-100 overflow-hidden group flex flex-col h-full transition-all duration-300"
    >
      {/* Image */}
      <div className="relative bg-gray-50 flex items-center justify-center p-6 h-56">
        <img
          src={product.image}
          alt={product.fullName}
          className="max-h-44 w-auto max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-3 right-3 text-xs bg-primary-100 text-primary-700 font-semibold px-3 py-1 rounded-full">
          {product.badge}
        </span>
        {product.category === 'kids' && (
          <span className="absolute top-3 left-3 text-xs bg-pink-100 text-pink-700 font-semibold px-3 py-1 rounded-full">
            Kids
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900">{product.fullName}</h3>
          <p className="text-gray-500 text-sm mt-2 leading-relaxed line-clamp-2">{product.shortDescription}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <span className="text-xs px-2.5 py-1 bg-green-50 text-green-700 rounded-full border border-green-100">
              {product.bristleType}
            </span>
            <span className="text-xs px-2.5 py-1 bg-gray-50 text-gray-600 rounded-full border border-gray-100">
              {UNITS_PER_BOX} units/box
            </span>
          </div>

          {/* Delivery */}
          <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
            <ClockIcon className="w-3.5 h-3.5" />
            <span>Delivery: {product.deliveryTimeline}</span>
          </div>
        </div>

        {/* Price + CTA */}
        <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Pricing</div>
            <p className="text-[10px] text-gray-500 font-medium">${product.pricePerBox.toFixed(0)} per box</p>
            <p className="text-[10px] text-primary-600 font-bold leading-tight">Optional Engraving: +$10/box</p>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">${product.pricePerBox.toFixed(2)}</p>
          </div>
          <button
            id={`shop-add-${product.slug}`}
            onClick={handleAdd}
            className={`inline-flex items-center gap-1.5 px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02] ${
              added 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {added ? 'Added!' : 'Add to Cart'}
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  )
}

export default Shop
